// The Increase box on the Record (S14, part 2 of the increase review; Record board, Oct 30), in the
// kit's handoff banner style (Banner card): an amber Increase tag, a title and one line, and the
// answer buttons on the right while nobody answered. Nothing locks: the VA sends the email as usual.
import { useState } from 'react';
import type { Api, IncreaseRule, RenewalRow } from '../auth/api';
import type { Role } from '../roles';
import { ConfirmPopup } from '../shell/ConfirmPopup';
import { Toast } from '../shell/Toast';
import {
  answerError,
  answerLabels,
  answeredToast,
  increaseBox,
  increaseButtons,
  reshopConfirm,
  type IncreaseAnswer,
} from './increase-lines';

export function IncreaseBox({
  api,
  r,
  rule,
  premium,
  role,
  onChanged,
}: {
  api: Api;
  r: RenewalRow;
  rule: IncreaseRule;
  /** The renewal premium the Record shows (the dec page's, read or corrected, otherwise AMS360's). */
  premium: number | null;
  role: Role;
  /** The answer or its Undo was saved: read the Record again. */
  onChanged: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ id: number; text: string; undo: boolean } | null>(null);
  const box = increaseBox(r, rule, premium);
  const buttons = increaseButtons(r, role);

  // A new key each time, so a second toast replaces the first one.
  const show = (text: string, undo: boolean) =>
    setToast((last) => ({ id: (last?.id ?? 0) + 1, text, undo }));

  const answer = async (a: IncreaseAnswer) => {
    setBusy(true);
    const res = await api.answerIncrease(r.id, a);
    setBusy(false);
    setConfirming(false);
    if (res.ok) show(answeredToast(a, r.insured), true);
    else show(answerError(res.error), false);
    onChanged();
  };

  const undo = async () => {
    const res = await api.undoIncreaseAnswer(r.id);
    show(res.ok ? 'Undone. Back where it was.' : answerError(res.error, true), false);
    onChanged();
  };

  return (
    <>
      {box && (
        <div className="bnr info" role="status" aria-label={box.title}>
          <span className="tag t-amber">Increase</span>
          <span className="col g2 grow">
            <span className="w6">{box.title}</span>
            <span>{box.line}</span>
          </span>
          {buttons.map((b) => (
            <button
              key={b}
              type="button"
              className={b === 'looked_over' ? 'btn btn-sm btn-primary' : 'btn btn-sm'}
              disabled={busy}
              aria-haspopup={b === 'reshop' ? 'dialog' : undefined}
              onClick={() => (b === 'reshop' ? setConfirming(true) : void answer(b))}
            >
              {answerLabels[b]}
            </button>
          ))}
        </div>
      )}
      {confirming && (
        <ConfirmPopup
          {...reshopConfirm(r.insured)}
          busy={busy}
          onConfirm={() => void answer('reshop')}
          onCancel={() => setConfirming(false)}
        />
      )}
      {toast && (
        <Toast
          key={toast.id}
          text={toast.text}
          action={toast.undo ? { label: 'Undo', onClick: () => void undo() } : undefined}
          shownMs={toast.undo ? 8000 : undefined}
        />
      )}
    </>
  );
}
